import React, { ComponentType, FC, ReactNode } from 'react';
import { StackProps, SxProps, Theme } from '@mui/material';

import { Stack } from '../Stack';
import { Typography, TypographyProps } from '../Typography';
import { PaletteColor } from '../../palette';


export type BadgeColors = 'green' | 'red' | 'purple' | 'orange' | 'gray' | 'lightGray';

const badgePrimaryBgColor: Record<BadgeColors, PaletteColor> = {
    green: 'infoCustom.green50',
    red: 'infoCustom.red50',
    purple: 'general.secondary50',
    orange: 'infoCustom.orange50',
    gray: 'grayscale.shade85',
    lightGray: 'grayscale.shade20',
};

const badgeSecondaryBgColor: Record<BadgeColors, PaletteColor> = {
    green: 'infoCustom.green10',
    red: 'infoCustom.red10',
    purple: 'grayscale.shade10',
    orange: 'infoCustom.orange10',
    gray: 'grayscale.shade10',
    lightGray: 'grayscale.shade20',
};

const badgeTypographyColor: Record<BadgeColors, PaletteColor> = {
    green: 'infoCustom.green60',
    red: 'infoCustom.red60',
    purple: 'grayscale.shade90',
    orange: 'infoCustom.orange60',
    gray: 'grayscale.shade90',
    lightGray: 'grayscale.shade20',
};

type BadgeSizes = 'large' | 'medium' | 'small';

const iconSizeSpacing: Record<BadgeSizes, number> = {
    large: 6,
    medium: 4,
    small: 3,
};

const propsBySize: Record<BadgeSizes, StackProps> = {
    large: {
        py: 2,
        px: 4,
    },
    medium: {
        py: 1.5,
        px: 4,
    },
    small: {
        py: 1,
        px: 3,
    },
};

const typographyVariantBySize: Record<BadgeSizes, TypographyProps['variant']> =
    {
        large: 'regularBody2',
        medium: 'regularCaption1',
        small: 'regularCaption3',
    };

export type BadgeProps = {
    color?: BadgeColors;
    view: 'primary' | 'secondary' | 'stroke';
    size: BadgeSizes;
    text?: string | ReactNode;
    leftIcon?: ComponentType<{sx: SxProps<Theme>}>;
    rightIcon?: ComponentType<{sx: SxProps<Theme>}>;
    sx?: SxProps;
};

export const Badge: FC<BadgeProps> = ({
    color = 'green',
    view,
    size,
    text,
    leftIcon: LeftIcon,
    rightIcon: RightIcon,
    sx = [],
}) => (
    <Stack
        direction='row'
        pr={RightIcon && 2}
        pl={LeftIcon && 2}
        spacing={2}
        borderRadius={10}
        color={
            view === 'primary'
                ? 'grayscale.shade0'
                : badgeTypographyColor[color]
        }
        {...propsBySize[size]}
        sx={[
            {
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: view === 'stroke' ? badgeTypographyColor[color] : 'transparent',
                bgcolor: view === 'primary'
                    ? badgePrimaryBgColor[color]
                    : view === 'secondary'
                        ? badgeSecondaryBgColor[color]
                        : 'grayscale.shade0',
            },
            ...Array.isArray(sx) ? sx : [sx],
        ]}
    >
        {LeftIcon && (
            <LeftIcon
                sx={({ spacing }) => ({
                    width: spacing(iconSizeSpacing[size]),
                    height: spacing(iconSizeSpacing[size]),
                })}
            />
        )}
        {text && (
            <Typography
                textAlign='center'
                variant={typographyVariantBySize[size]}
                whiteSpace='nowrap'
                color={
                    view === 'secondary'
                        ? badgeTypographyColor[color]
                        : 'grayscale.shade100'
                }
            >
                {text}
            </Typography>
        )}
        {RightIcon && (
            <RightIcon
                sx={({ spacing }) => ({
                    width: spacing(iconSizeSpacing[size]),
                    height: spacing(iconSizeSpacing[size]),
                })}
            />
        )}
    </Stack>
);
